'use client';

import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { API_URL, TOAST_STYLE_SUCCESS } from '../../utils/constants';
import auth from '../../Auth';
import { Circle, Loader2, Pencil, Play, Trash2 } from 'lucide-react';
import Breadcrumbs from '../Breadcrumbs';
import { Table, TableBody, TableColumn, TableHeader, TableRow, TableCell, Button } from '@nextui-org/react';
import { toast } from 'react-toastify';


const TestResults = () => {
  const { prompt_id, test_id } = useParams();

  const [testResults, setTestResults] = useState([])
  const [testName, setTestName] = useState('')
  const [isFetchedTestResults, setIsFetchedTestResults] = useState(false)

  const fetchTestResults = async () => {
      await axios.get(`${API_URL}/tests?getAllResults&prompt_id=${prompt_id}&test_id=${test_id}`, {
        headers: {
          Authorization: `Bearer ${await auth.getIdToken()}`
        }
      }).then((res) => {
        setTestResults(res.data.results)
        setTestName(res.data.test_name)
      }).catch((err) => console.error(err))
      setIsFetchedTestResults(true)
  }

  const handleDeleteTestResult = async (info) => {
    await axios.delete(`${API_URL}/tests?deleteTestResults&prompt_id=${prompt_id}&test_id=${test_id}&prompt_version=${info.split('#')[1]}&timestamp=${info.split('#')[0]}`,
      {
        headers: {
          Authorization: `Bearer ${await auth.getIdToken()}`,
          'Content-Type': 'application/json'
        }
      }).then(() => {
        toast.success('Result deleted successfully!', TOAST_STYLE_SUCCESS)
        fetchTestResults();
      })
  }

  // 2. fetch prompt data with version or timestamp from logs
  useEffect(() => {
    fetchTestResults()
    const intervalId = setInterval(fetchTestResults, 30_000);

    return () => clearInterval(intervalId);
  }, []);

  const crumbs = [
    { path: '/', breadcrumb: 'Home' },
    { path: `/tests`, breadcrumb: 'CI/CD' },
    { path: `/tests/${prompt_id}`, breadcrumb: `${prompt_id}` },
    { path: window.location.href, breadcrumb: `${testName}` }
  ];

  const handleRun = async (prompt_version, model_name, prompt_id, test_id) => {
    await axios.post(`${API_URL}/tests?runVersion`,
      {
        test_id,
        prompt_id,
        prompt_version,
        model_name,
      },
      {
        headers: {
          Authorization: `Bearer ${await auth.getIdToken()}`,
          'Content-Type': 'application/json'
        }
      }
    ).then((res) => {
      toast.success("Test started successfuly!", TOAST_STYLE_SUCCESS)
    }).catch((err) => console.error(err))
  }

  return (
    <div className='flex flex-col w-full p-8 space-y-8'>
    <Breadcrumbs crumbs={crumbs} />
        <div className='w-full'>
        {isFetchedTestResults ? 
            <>
            <div className='flex items-center gap-2'>
              <h2 className='font-bold text-xl'>{testName} </h2>
              <div className="relative group">
                <Pencil onClick={() => window.open(`/tests/edit/${prompt_id}/${test_id}`)} cursor='pointer' size={20}/>
                <span className="absolute left-1/2 transform -translate-x-1/2 bottom-full mb-1 hidden group-hover:block bg-gray-200 text-black text-xs rounded-lg px-2 py-1 whitespace-nowrap">
                    Edit test
                  </span>
              </div>
            </div>
            
            <Table isHeaderSticky className='mt-6 max-h-[75vh]' aria-label='Prompt Library Table'>
            <TableHeader>
                <TableColumn>Prompt version</TableColumn>
                <TableColumn>Test run time</TableColumn>
                <TableColumn>Model</TableColumn>
                
                <TableColumn>Latency</TableColumn>
                <TableColumn>Price</TableColumn>
                <TableColumn>Score</TableColumn>
                <TableColumn />
            </TableHeader>
            <TableBody>
                {testResults.map((testResult, index) => (
                <TableRow
                    className={!testResult.pending && !testResult.failed && 'cursor-pointer text-blue-500 hover:bg-gray-100'}
                    onClick={() => !testResult.pending && !testResult.failed && window.open(`/tests/${prompt_id}/${test_id}/${testResult.timestamp_prompt_version.split('#')[1]}/${testResult.timestamp_prompt_version.split('#')[0]}`, '_blank')}
                    key={testResult.env_id_test_id_prompt_id}>
                    <TableCell>{testResult.timestamp_prompt_version.split('#')[1]}</TableCell>
                    <TableCell>{testResult.timestamp_prompt_version.split('#')[0]}</TableCell>
                    <TableCell>{testResult.model_name}</TableCell>
                    <TableCell>
                      {testResult.metrics?.latency !== undefined 
                        ? `${Math.round((testResult.metrics.latency) / 10) / 100}s` 
                        : "-"}
                    </TableCell>
                    <TableCell>
                      {testResult.metrics?.price_of_call !== undefined 
                        ? `${Math.round(testResult.metrics.price_of_call * 10000) / 10000}$` 
                        : "-"}
                    </TableCell>
                    <TableCell>{testResult.pending ? 
                      <Circle size={14} className="text-orange-500 animate-pulse" fill="currentColor"/> : 
                      testResult.failed ? <span className='font-bold text-red-600'>Failed</span> :
                    <span className='font-bold text-black'>{testResult.score}%</span>}</TableCell>
                    <TableCell  onClick={(e) => e.stopPropagation()} 
                    className='flex items-center gap-2'>
                      <Play color='black' size={25} cursor={'pointer'} 
                      onClick={() => handleRun(testResult.timestamp_prompt_version.split('#')[1], testResult.model_name, prompt_id, test_id)}/>
                      <Trash2 color='red' size={25} cursor={'pointer'} onClick={() => handleDeleteTestResult(testResult.timestamp_prompt_version)}/></TableCell>
                </TableRow>
                ))}
            </TableBody>
            </Table> </> : <Loader2 className='m-auto mt-3 animate-spin' />
            }
            </div>
    </div>
  );
};

export default TestResults;
